/* ShowFacility.jsx – the owner's facility page (/ShowFacility/:id/:email):
   identity, address and business details (edited in place), the geolocation
   QR while the location isn't saved, the events, and the QRProof badge.

   Events: the list comes first (facilityEvents/EventList.jsx, grouped En cours /
   À venir / Passés et désactivés) with « Nouvel événement »; creating, editing
   and « Refaire aujourd'hui » happen in a dialog (facilityEvents/EventFormDialog.jsx).
   This page keeps loading and the /api/event calls.

   Communauté: the YummiTracker lists the place is in, its photos (with the
   owner's reply) and the share kit (community/FacilityCommunity.jsx). */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, TextField, Box, Paper, Grid,
  Alert, Button, Chip, Divider,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { initKeycloak } from '../keycloak';
import { useTranslation } from 'react-i18next';
import EventList from './facilityEvents/EventList';
import EventFormDialog from './facilityEvents/EventFormDialog';
import { EVENT_TYPE_VALUES, toLocalInput, fromLocalInput } from './facilityEvents/eventStatus';
import FacilityCommunity from './community/FacilityCommunity';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

// map facility_type -> translation key. Both registration forms
// (FacilityForm.jsx, FacilityForm_v2.jsx) actually submit the string
// values below (e.g. 'restaurant', 'fitness_center'), never the numeric
// 0-10/99 this used to be keyed by - that mismatch made every facility
// show "Unknown" here regardless of its real type. Numeric keys are kept
// for any legacy rows that predate the string-based forms.
const FACILITY_TYPE_KEYS = {
  restaurant: 'restaurant',
  gite: 'gite',
  bar: 'bar',
  hotel: 'hotel',
  museum: 'museum',
  shop: 'shop',
  pharmacy: 'pharmacy',
  bakery: 'bakery',
  activity: 'activity',
  camping: 'camping',
  fitness_center: 'gym',
  gym: 'gym',
  other: 'other',
  0: 'restaurant',
  1: 'gite',
  2: 'bar',
  3: 'hotel',
  4: 'museum',
  5: 'shop',
  6: 'pharmacy',
  7: 'bakery',
  8: 'activity',
  9: 'camping',
  10: 'gym',
  99: 'other'
};


export default function ShowFacility() {
  const { t } = useTranslation();
  const { id, email: emailParam = '' } = useParams();
  const navigate = useNavigate();

  const [token, setToken] = useState(sessionStorage.getItem('kc_token') || '');
  const [kcEmail, setKcEmail] = useState('');

  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState({});
  const handleBack = () => {
    navigate('/showDashboard', { replace: true });
  };

  // default event type: 3 (dish of the day) like before
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  // The create / edit dialog (EventFormDialog): id = the event being edited.
  const [dialog, setDialog] = useState({ open: false, mode: 'create', initial: null, id: null });

  const authorised = Boolean(emailParam) && Boolean(kcEmail) &&
    emailParam.toLowerCase() === kcEmail.toLowerCase();

  const ownerEmail = useMemo(() => kcEmail || emailParam || '', [kcEmail, emailParam]);

  // event types with translated labels
  const eventTypes = useMemo(
    () =>
      EVENT_TYPE_VALUES.map((et) => ({
        ...et,
        label: t(`showFacility.eventType.${et.key}`)
      })),
    [t]
  );

  const facilityTypeLabel = (ft) => {
    const key = FACILITY_TYPE_KEYS[ft] || 'unknown';
    const k = `showFacility.facilityType.${key}`;
    const translated = t(k);
    // if missing, fall back to key name
    return translated === k ? key : translated;
  };

  // auth
  useEffect(() => {
    if (token) return;
    (async () => {
      try {
        const kc = await initKeycloak();
        if (!kc.authenticated) {
          kc.login();
          return;
        }
        sessionStorage.setItem('kc_token', kc.token);
        setToken(kc.token || '');
        setKcEmail(kc.tokenParsed?.email ?? '');
      } catch {
        setError(t('showFacility.authError'));
        setLoading(false);
      }
    })();
  }, [token, t]);

  useEffect(() => {
    if (!token || kcEmail) return;
    try {
      const decoded = JSON.parse(atob(token.split('.')[1] || ''));
      if (decoded?.email) setKcEmail(decoded.email);
    } catch {
      // ignore
    }
  }, [token, kcEmail]);

  // « Refaire aujourd'hui »: a past announcement's title/type/message with its
  // time of day moved to today, in the dialog for a new event.
  const redoToday = (ev) => {
    const toToday = (dateStr) => {
      const base = new Date(dateStr);
      const d = new Date();
      d.setHours(base.getHours(), base.getMinutes(), 0, 0);
      return toLocalInput(d);
    };
    setDialog({
      open: true, mode: 'create', id: null,
      initial: { name: ev.name || '', type: ev.type, date: toToday(ev.date), end_date: toToday(ev.end_date), message: ev.message || '' },
    });
  };

  // load facility
  useEffect(() => {
    if (!id || !token || !authorised) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/ShowFacility/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error();
        setFacility(await r.json());
      } catch {
        setError(t('showFacility.loadError'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token, authorised, t]);

  const saveField = async (field, value) => {
    if (!token) return;
    setSaveStatus((s) => ({ ...s, [field]: 'saving' }));
    try {
      const r = await fetch(`${API_BASE_URL}/UpdateFacility/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: value }),
      });
      if (!r.ok) throw new Error();
      setFacility((f) => ({ ...f, [field]: value }));
      setSaveStatus((s) => ({ ...s, [field]: 'ok' }));
    } catch {
      setSaveStatus((s) => ({ ...s, [field]: 'error' }));
    } finally {
      setTimeout(
        () => setSaveStatus((s) => ({ ...s, [field]: undefined })),
        3000
      );
    }
  };

  // Ensures we always have the latest token from sessionStorage
  const getAuthToken = () => {
    const freshToken = sessionStorage.getItem('kc_token') || token;
    return freshToken || '';
  };

  const loadEvents = useCallback(async () => {
    if (!id || !token || !authorised || !ownerEmail) return;
    setEventsLoading(true);
    setEventsError('');
    try {
      const r = await fetch(`${API_BASE_URL}/event/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ facility_id: Number(id) }),
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setEvents(Array.isArray(data) ? data : data?.rows || []);
    } catch {
      setEventsError(t('showFacility.loadEventsError'));
    } finally {
      setEventsLoading(false);
    }
  }, [id, token, authorised, ownerEmail, t]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Create (POST /event) or update (PUT /event/:id) from the dialog. A failure
  // throws a translated message, which the dialog shows.
  const submitEvent = async (values) => {
    const authToken = getAuthToken();
    if (!authToken || !ownerEmail) throw new Error(t('showFacility.authError'));
    const editing = dialog.mode === 'edit' && dialog.id != null;
    const body = {
      name: values.name,
      type: values.type,
      date: fromLocalInput(values.date),
      end_date: fromLocalInput(values.end_date),
      message: values.message,
      email_owner: ownerEmail,
      ...(editing ? {} : { facility_id: Number(id) }),
    };
    const r = await fetch(`${API_BASE_URL}/event${editing ? `/${dialog.id}` : ''}`, {
      method: editing ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      console.error(`${editing ? 'Update' : 'Create'} event error: HTTP ${r.status}`, await r.text().catch(() => ''));
      throw new Error(t(editing ? 'showFacility.updateError' : 'showFacility.createEventError'));
    }
    setDialog((d) => ({ ...d, open: false }));
    await loadEvents();
  };

  // ✅ archive = deactivate (activated=0)
  const archiveEvent = async (rowId) => {
    const authToken = getAuthToken();
    if (!authToken || !ownerEmail) return;
    try {
      const r = await fetch(`${API_BASE_URL}/event/${rowId}/archive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ email_owner: ownerEmail }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await loadEvents();
    } catch (e) {
      console.error('Archive event error:', e);
      setEventsError(t('showFacility.archiveError'));
    }
  };

  // ✅ unarchive = activate (activated=1)
  const unarchiveEvent = async (rowId) => {
    const authToken = getAuthToken();
    if (!authToken || !ownerEmail) return;
    try {
      const r = await fetch(`${API_BASE_URL}/event/${rowId}/unarchive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ email_owner: ownerEmail }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await loadEvents();
    } catch (e) {
      console.error('Unarchive event error:', e);
      setEventsError(t('showFacility.unarchiveError'));
    }
  };

  const deleteEvent = async (rowId) => {
    const authToken = getAuthToken();
    if (!authToken || !ownerEmail) return;
    const ok = window.confirm(t('showFacility.deleteConfirm'));
    if (!ok) return;

    try {
      const r = await fetch(`${API_BASE_URL}/event/${rowId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ email_owner: ownerEmail }),
      });
      if (!r.ok) {
        const tResp = await r.text();
        throw new Error(`HTTP ${r.status}: ${tResp}`);
      }
      await loadEvents();
    } catch (e) {
      console.error('Delete event error:', e);
      setEventsError(t('showFacility.deleteError'));
    }
  };

  const Editable = ({ label, field }) => (
    <Grid item xs={12} sm={6}>
      <TextField
        label={label}
        fullWidth
        defaultValue={facility?.[field] || ''}
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v !== (facility?.[field] || '')) saveField(field, v);
        }}
      />
      {saveStatus[field] === 'ok' && (
        <Chip size="small" label="✔" color="success" sx={{ ml: 1, mt: 1 }} />
      )}
      {saveStatus[field] === 'error' && (
        <Chip size="small" label="✖" color="error" sx={{ ml: 1, mt: 1 }} />
      )}
    </Grid>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Button
        variant="outlined"
        startIcon={<span style={{ fontWeight: 600 }}>←</span>}
        sx={{ mb: 2 }}
        onClick={handleBack}
      >
        {t('showFacility.back')}
      </Button>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t('showFacility.title')}
        </Typography>

        {loading && <Typography>{t('showFacility.loading')}</Typography>}
        {!loading && !authorised && (
          <Alert severity="error">{t('showFacility.forbidden')}</Alert>
        )}
        {error && authorised && <Alert severity="error">{error}</Alert>}

        {authorised && facility && (
          <>
            {/* Identité */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6">
                {t('showFacility.sectionIdentity')}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('showFacility.name')}
                    fullWidth
                    value={facility.name || ''}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('showFacility.description')}
                    fullWidth
                    multiline
                    minRows={3}
                    value={facility.description || ''}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Editable label={t('showFacility.email')} field="emailAddress" />
                <Editable label={t('showFacility.phone')} field="contact_phone" />
              </Grid>

              {/* Adresse */}
              <Typography variant="h6">
                {t('showFacility.sectionAddress')}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {['address', 'lon', 'lat'].map((f) => (
                  <Grid item xs={12} sm={6} key={f}>
                    <TextField
                      label={f}
                      fullWidth
                      value={facility[f] || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Entreprise */}
              <Typography variant="h6">
                {t('showFacility.sectionBusiness')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('showFacility.type')}
                    fullWidth
                    value={facilityTypeLabel(facility.facility_type)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Editable label={t('showFacility.reservationUrl')} field="business_reservation_url" />
                <Editable label={t('showFacility.website')} field="business_url" />
              </Grid>
            </Box>

            {/* QR géolocalisation */}
            {facility.current_registration_step === 'unstarted' && (
              <Box sx={{ textAlign: 'center', mt: 5 }}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  {t('showFacility.geoWarning')}
                </Alert>
                <Typography variant="h6" gutterBottom>
                  {t('showFacility.geoInstruction')}
                </Typography>
                <QRCodeSVG
                  value={`${API_BASE_URL}/v2/SetLocation/id/${id}`}
                  size={180}
                />
              </Box>
            )}

            {/* Events */}
            <Divider sx={{ my: 4 }} />
            <Typography variant="h5">
              {t('showFacility.eventsTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {t('showFacility.eventsSectionIntro')}
            </Typography>

            <EventList
              events={events}
              loading={eventsLoading}
              eventTypes={eventTypes}
              onNew={() => setDialog({ open: true, mode: 'create', initial: null, id: null })}
              onRedo={redoToday}
              onEdit={(ev) => setDialog({
                open: true, mode: 'edit', id: ev.id,
                initial: { name: ev.name ?? '', type: ev.type, date: toLocalInput(ev.date), end_date: toLocalInput(ev.end_date), message: ev.message ?? '' },
              })}
              onArchive={archiveEvent}
              onUnarchive={unarchiveEvent}
              onDelete={deleteEvent}
            />
            <EventFormDialog
              open={dialog.open}
              mode={dialog.mode}
              initial={dialog.initial}
              facilityName={facility.name}
              facilityType={facility.facility_type}
              eventTypes={eventTypes}
              onSubmit={submitEvent}
              onClose={() => setDialog((d) => ({ ...d, open: false }))}
            />

            {eventsError && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">{eventsError}</Alert>
              </Box>
            )}

            {/* Communauté YummiTracker */}
            <Divider sx={{ my: 4 }} />
            <Typography variant="h5">{t('community.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {t('community.intro')}
            </Typography>
            <FacilityCommunity facilityId={id} token={token} />

            {/* Badge QRProof — moved to the bottom of the page; owners now
                land on facility management first, badge embed code last. */}
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                mt: 4,
                borderRadius: 3,
                backgroundColor: '#f8fbff',
                borderColor: '#d6e8ff',
              }}
            >
              <Typography variant="h6" gutterBottom>
                🌟 {t('dashboard.badgeHelp.title')}
              </Typography>

              <Typography variant="body2" color="text.secondary" paragraph>
                {t('dashboard.badgeHelp.description')}
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                {t('dashboard.badgeHelp.instructions')}
              </Alert>

              <Typography variant="subtitle2" gutterBottom>
                {t('dashboard.badgeHelp.previewTitle')}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <img
                  src={`${API_BASE_URL}/public/light-badge/${id}.svg`}
                  alt={t('dashboard.badgeHelp.imageAlt')}
                  style={{ maxWidth: 360, width: '100%' }}
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                {t('dashboard.badgeHelp.htmlTitle')}
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: '#1e1e1e',
                  color: '#ffffff',
                  overflowX: 'auto',
                  fontFamily: 'monospace',
                  fontSize: 13,
                  whiteSpace: 'pre-wrap',
                }}
              >
{`<img
    src="https://qrproofs.com/api/public/light-badge/${id}.svg"
    alt="${t('dashboard.badgeHelp.imageAlt')}">`}
              </Paper>

              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={async () => {
                  const badgeHtml =
`<a href="https://qrproofs.com/facility/${id}" target="_blank" rel="noopener noreferrer">
  <img
    src="https://qrproofs.com/api/public/light-badge/${id}.svg"
    alt="${t('dashboard.badgeHelp.imageAlt')}">
</a>`;

                  try {
                    await navigator.clipboard.writeText(badgeHtml);
                    alert(t('dashboard.badgeHelp.copySuccess'));
                  } catch (error) {
                    console.error('[badge copy] failed:', error);
                    alert(t('dashboard.badgeHelp.copyFailed'));
                  }
                }}
              >
                📋 {t('dashboard.badgeHelp.copyButton')}
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('dashboard.badgeHelp.footer')}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('dashboard.badgeHelp.inactiveNotice')}
              </Typography>
            </Paper>
          </>
        )}
      </Paper>
    </Container>
  );
}
